import React, { useCallback, useState } from 'react';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { IconEditPaperSvg } from 'apps/sigma2-ui/src/components/svg/IconEditPaper';
import { SearchableFilterListBoxProps } from 'apps/sigma2-ui/src/filters-v2/components/SearchableFilterListBox';
import { SearchableFilterListBox } from 'apps/sigma2-ui/src/filters-v2/components/SearchableFilterListBox';
import cn from 'classnames';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Spinner } from 'apps/sigma2-ui/src/components/ui/Spinner';
import { IconCheckMark24pxSvg } from 'apps/sigma2-ui/src/components/svg/IconCheckMark24px';
import { IconCloseSvg } from 'apps/sigma2-ui/src/components/svg/IconClose';
import { useDashboards } from 'apps/sigma2-ui/src/lib/hooks/useDashboards';

export const DashboardsSearchableFilterListBox = ({
  multiple,
  items,
  onChange,
}: SearchableFilterListBoxProps) => {
  const language = useLanguage();

  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false);

  const handleClose = useCallback(() => {
    setIsCreatingDashboard(false);
  }, []);

  const searchWithCreateButton = useCallback(
    ({ searchBox }: { searchBox: React.ReactNode }) => {
      if (isCreatingDashboard) {
        return <CreateDashboardInlineForm handleClose={handleClose} />;
      }

      return (
        <div className="flex items-center gap-2">
          <div className="flex-1 grow ">{searchBox}</div>
          <PrimaryButtonLightBg
            translatedContent={language.new()}
            onClick={() => {
              setIsCreatingDashboard(true);
            }}
            icon={() => (
              <IconEditPaperSvg svg={{ className: 'text-white w-4 h-4' }} />
            )}
            disabled={false}
            innerClassName="!px-4"
          />
        </div>
      );
    },
    [setIsCreatingDashboard, language, isCreatingDashboard, handleClose],
  );

  return (
    <SearchableFilterListBox
      multiple={multiple}
      items={items}
      onChange={onChange}
      renderTopSection={searchWithCreateButton}
    />
  );
};

const CreateDashboardInlineForm = ({
  handleClose,
}: {
  handleClose: () => void;
}) => {
  const language = useLanguage();
  const [value, setValue] = useState('');

  const [isUpdating, setIsUpdating] = useState(false);

  const { createEmptyDashboard } = useDashboards();

  const handleSave = useCallback(async () => {
    if (value === '') {
      return;
    }

    setIsUpdating(true);
    await createEmptyDashboard({
      dashboard: {
        name: value,
        charts: [],
        isPublic: false,
        globalFilters: null,
        databaseRevisionId: null,
      },
    });

    setIsUpdating(false);
    handleClose();
  }, [value, createEmptyDashboard, handleClose]);

  const handleCancel = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const handleKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        await handleCancel();
        e.preventDefault();
      } else if (e.key === 'Enter') {
        await handleSave();
      }
    },
    [handleCancel, handleSave],
  );

  return (
    <div className="flex items-center gap-2">
      <div className="inline-block w-full relative">
        <IconEditPaperSvg
          svg={{
            className:
              'absolute top-1/2 left-3 transform -translate-y-1/2 w-5 h-5 text-gray-400',
          }}
        />

        <input
          type={'text'}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={language.newDashboard()}
          className={cn(
            'form-input w-full border-[1px] border-gray-200 rounded px-3 py-2 pl-11 focus:outline-none text-gray-900 placeholder:text-gray-600 text-xs font-sans font-normal leading-normal focus:ring-0 focus-visible:outline-none focus-visible:ring-0 focus:border-blue-500 focus-visible:border-blue-500 transition-colors',
            {
              'pr-11': value !== '',
            },
          )}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          innerClassName={['border-0 !px-2 bg-white !ring-0 !ring-offset-0']}
          size="small"
          role="button"
          disabled={isUpdating || value === ''}
          behavior={{
            type: 'onClick',
            onClick: handleSave,
          }}
        >
          {isUpdating ? (
            <Spinner size={16} textColor="text-gray-900" />
          ) : (
            <IconCheckMark24pxSvg svg={{ width: 18, height: 18 }} />
          )}
        </Button>
        <Button
          variant="outline"
          innerClassName={['border-0 !px-2 bg-white !ring-0 !ring-offset-0']}
          size="small"
          role="button"
          behavior={{
            type: 'onClick',
            onClick: handleCancel,
          }}
        >
          <IconCloseSvg svg={{ width: 18, height: 18 }} />
        </Button>
      </div>
    </div>
  );
};
